"use client";

import * as React from "react";

// @ts-nocheck
// @ts-ignore embla-carousel-react - package may not be installed
import useEmblaCarousel, {
  // @ts-ignore
  type UseEmblaCarouselType,
} from "embla-carousel-react";
import { ArrowLeft, ArrowRight } from "lucide-react";

import { cn } from "./utils";
import { Button } from "./button";

type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

type CarouselProps = {
  opts?: CarouselOptions;
  plugins?: CarouselPlugin;
  orientation?: "horizontal" | "vertical";
  setApi?: (api: CarouselApi) => void;
  /** Number of slides visible at once (e.g. 3 for three-column layout). Default 1. */
  slidesPerView?: number;
};

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  api: ReturnType<typeof useEmblaCarousel>[1];
  scrollPrev: () => void;
  scrollNext: () => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
  slidesPerView: number;
} & CarouselProps;

const CarouselContext = React.createContext<CarouselContextProps | null>(null);

function useCarousel() {
  const context = React.useContext(CarouselContext);

  if (!context) {
    throw new Error("useCarousel must be used within a <Carousel />");
  }

  return context;
}

function Carousel({
  orientation = "horizontal",
  opts,
  setApi,
  plugins,
  slidesPerView = 1,
  className,
  children,
  ...props
}: React.ComponentProps<"div"> & CarouselProps) {
  const [carouselRef, api] = useEmblaCarousel(
    {
      ...opts,
      axis: orientation === "horizontal" ? "x" : "y",
    },
    plugins
  );
  const [canScrollPrev, setCanScrollPrev] = React.useState(false);
  const [canScrollNext, setCanScrollNext] = React.useState(false);

  const onSelect = React.useCallback((api: CarouselApi) => {
    if (!api) {
      return;
    }
    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());
  }, []);

  const scrollPrev = React.useCallback(() => {
    api?.scrollPrev();
  }, [api]);

  const scrollNext = React.useCallback(() => {
    api?.scrollNext();
  }, [api]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        scrollPrev();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        scrollNext();
      }
    },
    [scrollPrev, scrollNext]
  );

  React.useEffect(() => {
    if (!api || !setApi) {
      return;
    }
    setApi(api);
  }, [api, setApi]);

  React.useEffect(() => {
    if (!api) {
      return;
    }
    onSelect(api);
    api.on("reInit", onSelect);
    api.on("select", onSelect);

    return () => {
      api?.off("select", onSelect);
    };
  }, [api, onSelect]);

  return (
    <CarouselContext.Provider
      value={{
        carouselRef,
        api: api,
        opts,
        slidesPerView,
        orientation:
          orientation || (opts?.axis === "y" ? "vertical" : "horizontal"),
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext,
      }}
    >
      <div
        onKeyDownCapture={handleKeyDown}
        className={cn("relative", className)}
        role="region"
        aria-roledescription="carousel"
        data-slot="carousel"
        {...props}
      >
        {children}
      </div>
    </CarouselContext.Provider>
  );
}

function CarouselContent({ className, ...props }: React.ComponentProps<"div">) {
  const { carouselRef, orientation, slidesPerView } = useCarousel();
  const isMultiColumn = slidesPerView > 1 && orientation === "horizontal";

  return (
    <div
      ref={carouselRef}
      className="overflow-hidden"
      data-slot="carousel-content"
    >
      <div
        className={cn(
          "flex",
          orientation === "horizontal" ? "-ml-4" : "-mt-4 flex-col",
          isMultiColumn && "gap-4",
          className
        )}
        {...props}
      />
    </div>
  );
}

function CarouselItem({ className, ...props }: React.ComponentProps<"div">) {
  const { orientation, slidesPerView } = useCarousel();
  const isMultiColumn = slidesPerView > 1 && orientation === "horizontal";

  const basisClass =
    isMultiColumn && slidesPerView === 3
      ? "basis-full md:basis-[calc((100%-1rem)/2)] lg:basis-[calc((100%-2rem)/3)]" // 1 col mobile, 2 col md, 3 col lg; gap-4
      : isMultiColumn && slidesPerView === 2
        ? "basis-full sm:basis-[calc((100%-1rem)/2)]"
        : "basis-full";

  return (
    <div
      role="group"
      aria-roledescription="slide"
      data-slot="carousel-item"
      className={cn(
        "min-w-0 shrink-0 grow-0",
        basisClass,
        orientation === "horizontal" && !isMultiColumn ? "pl-4" : "",
        orientation === "horizontal" && isMultiColumn ? "pl-0" : "",
        orientation === "vertical" ? "pt-4" : "",
        className
      )}
      {...props}
    />
  );
}

function CarouselPrevious({
  className,
  variant = "outline",
  size = "icon",
  ...props
}: React.ComponentProps<typeof Button>) {
  const { orientation, scrollPrev, canScrollPrev, opts } = useCarousel();
  const isLoop = opts?.loop === true;

  return (
    <Button
      data-slot="carousel-previous"
      variant={variant}
      size={size}
      className={cn(
        "absolute size-8 rounded-full",
        orientation === "horizontal"
          ? "-left-12 top-1/2 -translate-y-1/2"
          : "-top-12 left-1/2 -translate-x-1/2 rotate-90",
        className
      )}
      disabled={isLoop ? false : !canScrollPrev}
      onClick={scrollPrev}
      {...props}
    >
      <ArrowLeft />
      <span className="sr-only">Previous slide</span>
    </Button>
  );
}

function CarouselNext({
  className,
  variant = "outline",
  size = "icon",
  ...props
}: React.ComponentProps<typeof Button>) {
  const { orientation, scrollNext, canScrollNext, opts } = useCarousel();
  const isLoop = opts?.loop === true;

  return (
    <Button
      data-slot="carousel-next"
      variant={variant}
      size={size}
      className={cn(
        "absolute size-8 rounded-full",
        orientation === "horizontal"
          ? "-right-12 top-1/2 -translate-y-1/2"
          : "-bottom-12 left-1/2 -translate-x-1/2 rotate-90",
        className
      )}
      disabled={isLoop ? false : !canScrollNext}
      onClick={scrollNext}
      {...props}
    >
      <ArrowRight />
      <span className="sr-only">Next slide</span>
    </Button>
  );
}

type CarouselDotsProps = {
  count: number;
  className?: string;
  dotClassName?: string;
  activeDotClassName?: string;
};

function CarouselDots({
  count,
  className = "mt-8 flex justify-center gap-2",
  dotClassName = "h-2 rounded-full transition-all w-2 bg-[#D4A017]/40 hover:bg-[#D4A017]/60",
  activeDotClassName = "w-8 bg-[#D4A017]",
}: CarouselDotsProps) {
  const { api } = useCarousel();
  const [selectedIndex, setSelectedIndex] = React.useState(0);

  React.useEffect(() => {
    if (!api) {
      return;
    }
    setSelectedIndex(api.selectedScrollSnap());
    const onSelect = () => setSelectedIndex(api.selectedScrollSnap());
    api.on("select", onSelect);
    return () => {
      void api.off("select", onSelect);
    };
  }, [api]);

  return (
    <div className={className}>
      {Array.from({ length: count }, (_, i) => ({
        id: `dot-${i}`,
        index: i,
      })).map(({ id, index: i }) => (
        <button
          key={id}
          type="button"
          onClick={() => api?.scrollTo(i)}
          className={cn(
            "h-2 rounded-full transition-all",
            i === selectedIndex ? activeDotClassName : dotClassName
          )}
          aria-label={`Go to slide ${i + 1}`}
        />
      ))}
    </div>
  );
}

type CarouselAutoplayProps = {
  delayMs?: number;
  startAfterMs?: number;
};

function CarouselAutoplay({
  delayMs = 4000,
  startAfterMs = 500,
}: CarouselAutoplayProps) {
  const { api } = useCarousel();

  React.useEffect(() => {
    if (!api) {
      return;
    }
    let clearAutoplay: (() => void) | undefined;
    const timeoutId = window.setTimeout(() => {
      const interval = setInterval(() => api.scrollNext(), delayMs);
      clearAutoplay = () => clearInterval(interval);
    }, startAfterMs);
    return () => {
      window.clearTimeout(timeoutId);
      clearAutoplay?.();
    };
  }, [api, delayMs, startAfterMs]);

  return null;
}

export {
  type CarouselApi,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  CarouselDots,
  CarouselAutoplay,
};
